import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { usePreferences } from '@/stores/preferences-store';
import { formatDateTime, formatMoney } from '@/utils/format';

export interface CreditTopUpDetail {
  id: string;
  paymentReference: string;
  providerTransactionId: string | null;
  status: 'pending' | 'processing' | 'paid' | 'failed' | 'cancelled' | 'expired';
  creditAmountMinor: number;
  amountMinor: number;
  currency: string;
  failureCode: string | null;
  paidAt: string | null;
  createdAt: string;
  ledgerEntryId: string | null;
  testMode: boolean;
  canRetry: boolean;
  checkoutUrl: string | null;
  expiresAt: string;
}

type ProviderCheckStatus =
  | 'pending'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'expired'
  | 'refunded';

interface CreditTopUpRecheckResult {
  session: CreditTopUpDetail;
  providerCheck: {
    status: ProviderCheckStatus;
    confirmedAmountMinor: number | null;
    currency: string | null;
    method: 'mobile_money' | 'card' | null;
    channel: string | null;
    externalReference: string | null;
    feeMinor: number | null;
    providerReference: string | null;
    credentialSource: 'platform' | 'test';
    failureCode: string | null;
    failureReason: string | null;
    providerStatusLabel: string | null;
    processorResult: 'successful' | 'pending' | 'failed' | 'expired' | 'cancelled' | 'refunded';
  };
  outcome:
    | 'credits_added'
    | 'status_updated'
    | 'unchanged'
    | 'verified'
    | 'provider_pending'
    | 'provider_failed';
}

const statusBadgeVariant = (status: CreditTopUpDetail['status']): BadgeVariant => {
  if (status === 'paid') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'cancelled' || status === 'expired') return 'neutral';
  return 'info';
};

const providerStatusBadgeVariant = (status: ProviderCheckStatus): BadgeVariant => {
  if (status === 'succeeded') return 'success';
  if (status === 'failed' || status === 'cancelled' || status === 'expired' || status === 'refunded') {
    return 'danger';
  }
  return 'info';
};

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid gap-1 border-b border-border py-3 last:border-b-0 sm:grid-cols-[9rem_1fr] sm:gap-4">
    <dt className="text-body-sm text-fg-secondary">{label}</dt>
    <dd className="text-body text-fg">{children}</dd>
  </div>
);

const CopyableId = ({
  value,
  copyLabel,
  copiedLabel,
  copyFailedLabel,
}: {
  value: string;
  copyLabel: string;
  copiedLabel: string;
  copyFailedLabel: string;
}) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(copiedLabel);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(copyFailedLabel);
    }
  };

  return (
    <div className="flex items-start gap-2">
      <span className="min-w-0 flex-1 break-all font-mono text-body-sm">{value}</span>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="shrink-0"
        aria-label={copyLabel}
        onClick={() => void copy()}
      >
        {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      </Button>
    </div>
  );
};

const formatMethod = (
  method: CreditTopUpRecheckResult['providerCheck']['method'],
  channel: string | null | undefined,
  t: (key: string) => string,
): string => {
  if (!method) return '—';
  const label = t(`credits.topUpDetail.paymentMethods.${method}`);
  return channel?.trim() ? `${label} · ${channel.trim()}` : label;
};

const resolveFailureMessage = (
  failureCode: string | null,
  failureReason: string | null,
  t: (key: string, options?: { defaultValue?: string }) => string,
  tErrors: (key: string) => string,
): string | null => {
  if (failureReason) {
    const reasonKey = `credits.topUpDetail.providerReasons.${failureReason}`;
    const reasonLabel = t(reasonKey, { defaultValue: '' });
    if (reasonLabel) return reasonLabel;
  }
  if (!failureCode) return null;
  return tErrors(failureCode === 'UNKNOWN' ? 'PAYMENT_FAILED_UNKNOWN' : failureCode);
};

const shouldAutoRecheckOnOpen = (status: CreditTopUpDetail['status'] | null | undefined): boolean =>
  status != null && status !== 'paid' && status !== 'cancelled' && status !== 'expired';

interface CreditTopUpDetailDialogProps {
  sessionId: string | null;
  initialStatus?: CreditTopUpDetail['status'] | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreditTopUpDetailDialog = ({
  sessionId,
  initialStatus = null,
  open,
  onOpenChange,
}: CreditTopUpDetailDialogProps) => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const locale = usePreferences((state) => state.locale);
  const timezone = usePreferences((state) => state.timezone);

  const [displaySession, setDisplaySession] = useState<CreditTopUpDetail | null>(null);
  const [providerCheck, setProviderCheck] = useState<CreditTopUpRecheckResult['providerCheck'] | null>(
    null,
  );
  const [recheckOutcome, setRecheckOutcome] = useState<CreditTopUpRecheckResult['outcome'] | null>(
    null,
  );
  const autoRecheckStarted = useRef(false);
  const isManualRecheckRef = useRef(false);

  const detail = useQuery({
    queryKey: ['messaging', 'credits', 'checkout-sessions', sessionId],
    queryFn: () => api.get<CreditTopUpDetail>(`/messaging/credits/checkout-sessions/${sessionId}`),
    enabled: open && sessionId != null,
  });

  useEffect(() => {
    setDisplaySession(null);
    setProviderCheck(null);
    setRecheckOutcome(null);
    autoRecheckStarted.current = false;
  }, [sessionId]);

  useEffect(() => {
    if (detail.data && detail.data.id === sessionId) {
      setDisplaySession(detail.data);
    }
  }, [detail.data, sessionId]);

  const recheck = useMutation({
    mutationFn: (targetSessionId: string) =>
      api.post<CreditTopUpRecheckResult>(
        `/messaging/credits/checkout-sessions/${targetSessionId}/recheck`,
        {},
      ),
    onSuccess: async (result, targetSessionId) => {
      if (result.session.id !== targetSessionId) return;

      setDisplaySession(result.session);
      setProviderCheck(result.providerCheck);
      setRecheckOutcome(result.outcome);

      const shouldRefresh =
        result.outcome === 'credits_added' || result.outcome === 'status_updated';
      if (shouldRefresh) {
        await queryClient.invalidateQueries({ queryKey: ['messaging', 'credits'] });
        await queryClient.invalidateQueries({ queryKey: ['messaging', 'credits', 'ledger'] });
        await queryClient.invalidateQueries({
          queryKey: ['messaging', 'credits', 'checkout-sessions'],
        });
        await queryClient.invalidateQueries({
          queryKey: ['messaging', 'credits', 'checkout-sessions', targetSessionId],
        });
      }

      const shouldToast = isManualRecheckRef.current || result.outcome === 'credits_added';
      if (shouldToast) {
        if (result.outcome === 'credits_added') {
          toast.success(t('credits.purchaseSuccess'));
        } else if (result.outcome === 'provider_failed') {
          toast.info(t('credits.recheckFailed'));
        } else if (result.outcome === 'provider_pending') {
          toast.info(t('credits.purchasePending'));
        } else if (result.session.status === 'expired') {
          toast.info(t('credits.purchaseExpired'));
        }
      }
      isManualRecheckRef.current = false;
    },
    onError: (err) => {
      isManualRecheckRef.current = false;
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const session = displaySession ?? (detail.data?.id === sessionId ? detail.data : null);
  const canRecheck =
    session != null && session.status !== 'paid' && session.status !== 'cancelled' && session.status !== 'expired';
  const canContinuePayment = session?.canRetry === true && session.checkoutUrl != null;
  const statusForAutoRecheck = session?.status ?? initialStatus;

  useEffect(() => {
    if (!open || !sessionId || recheck.isPending || autoRecheckStarted.current) return;
    if (!shouldAutoRecheckOnOpen(statusForAutoRecheck)) return;
    autoRecheckStarted.current = true;
    recheck.mutate(sessionId);
  }, [open, sessionId, statusForAutoRecheck, recheck.isPending]);

  const check = providerCheck;
  const currency = check?.currency ?? session?.currency ?? 'GHS';
  const providerTransactionId =
    check?.providerReference ?? session?.providerTransactionId ?? null;
  const failureMessage = resolveFailureMessage(
    check?.failureCode ?? session?.failureCode ?? null,
    check?.failureReason ?? null,
    t,
    tErrors,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('credits.topUpDetail.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody contentClassName="gap-0">
          {detail.isLoading && !session ? (
            <p className="py-4 text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
          ) : detail.isError && !session ? (
            <p className="py-4 text-body-sm text-fg-secondary">{tCommon('state.error')}</p>
          ) : session ? (
            <>
              <p className="pb-4 text-body-sm text-fg-secondary">{t('credits.topUpDetail.intro')}</p>
              <dl>
                <DetailRow label={t('credits.topUpColumns.status')}>
                  <Badge variant={statusBadgeVariant(session.status)}>
                    {t(`credits.checkoutStatus.${session.status}`)}
                  </Badge>
                </DetailRow>
                <DetailRow label={t('credits.topUpColumns.amount')}>
                  {formatMoney(session.amountMinor, session.currency, { locale })}
                </DetailRow>
                <DetailRow label={t('credits.topUpDetail.creditsAdded')}>
                  {formatMoney(session.creditAmountMinor, session.currency, { locale })}
                </DetailRow>
                <DetailRow label={t('credits.topUpColumns.date')}>
                  {formatDateTime(session.createdAt, { locale, timezone })}
                </DetailRow>
                {session.paidAt ? (
                  <DetailRow label={t('credits.topUpDetail.paidAt')}>
                    {formatDateTime(session.paidAt, { locale, timezone })}
                  </DetailRow>
                ) : null}
                {failureMessage && !check ? (
                  <DetailRow label={t('credits.topUpDetail.failureReason')}>
                    <span className="text-danger">{failureMessage}</span>
                  </DetailRow>
                ) : null}
                {session.testMode ? (
                  <DetailRow label={t('credits.topUpDetail.environment')}>
                    {t('credits.testCheckoutNote')}
                  </DetailRow>
                ) : null}

                <div className="pt-4">
                  <p className="text-body-sm font-medium text-fg">
                    {t('credits.topUpDetail.recheck.confirmationSection')}
                  </p>
                </div>

                {recheck.isPending && !check ? (
                  <p className="py-3 text-body-sm text-fg-secondary">
                    {t('credits.topUpDetail.recheck.checking')}
                  </p>
                ) : null}

                {check || providerTransactionId ? (
                  <>
                    {check?.providerStatusLabel ? (
                      <DetailRow label={t('credits.topUpDetail.recheck.providerStatus')}>
                        <Badge variant={providerStatusBadgeVariant(check.status)}>
                          {check.providerStatusLabel}
                        </Badge>
                      </DetailRow>
                    ) : check ? (
                      <DetailRow label={t('credits.topUpDetail.recheck.providerStatus')}>
                        <Badge variant={providerStatusBadgeVariant(check.status)}>
                          {t(`credits.topUpDetail.recheck.providerStatuses.${check.status}`)}
                        </Badge>
                      </DetailRow>
                    ) : null}
                    <DetailRow label={t('credits.topUpDetail.recheck.processorResult')}>
                      {check ? (
                        <span
                          className={
                            check.processorResult === 'successful'
                              ? 'font-medium text-success-fg'
                              : check.processorResult === 'pending'
                                ? 'text-fg-secondary'
                                : 'text-danger'
                          }
                        >
                          {t(`credits.topUpDetail.recheck.processorResults.${check.processorResult}`)}
                        </span>
                      ) : session.status === 'paid' ? (
                        <span className="font-medium text-success-fg">
                          {t('credits.topUpDetail.recheck.processorResults.successful')}
                        </span>
                      ) : (
                        '—'
                      )}
                    </DetailRow>
                    {check ? (
                      <DetailRow label={t('credits.topUpDetail.paymentMethod')}>
                        {formatMethod(check.method, check.channel, t)}
                      </DetailRow>
                    ) : null}
                    {check?.externalReference ? (
                      <DetailRow label={t('credits.topUpDetail.recheck.receiptNumber')}>
                        <span className="break-all font-mono text-body-sm">{check.externalReference}</span>
                      </DetailRow>
                    ) : null}
                    {check?.confirmedAmountMinor != null ? (
                      <DetailRow label={t('credits.topUpDetail.recheck.confirmedAmount')}>
                        {formatMoney(check.confirmedAmountMinor, currency, { locale })}
                      </DetailRow>
                    ) : null}
                    {failureMessage && check ? (
                      <DetailRow label={t('credits.topUpDetail.recheck.providerResult')}>
                        <span className="text-danger">{failureMessage}</span>
                      </DetailRow>
                    ) : null}
                    {recheckOutcome ? (
                      <DetailRow label={t('credits.topUpDetail.recheck.outcomeLabel')}>
                        {t(`credits.topUpDetail.recheck.outcome.${recheckOutcome}`)}
                      </DetailRow>
                    ) : null}
                  </>
                ) : (
                  <p className="py-3 text-body-sm text-fg-secondary">
                    {t('credits.topUpDetail.recheck.confirmationEmpty')}
                  </p>
                )}

                <div className="pt-4">
                  <p className="text-body-sm font-medium text-fg">{t('credits.topUpDetail.referencesTitle')}</p>
                  <p className="mt-1 text-caption text-fg-secondary">
                    {t('credits.topUpDetail.referencesHelp')}
                  </p>
                </div>

                <DetailRow label={t('credits.topUpDetail.topUpId')}>
                  <CopyableId
                    value={session.id}
                    copyLabel={t('credits.topUpDetail.copyTopUpId')}
                    copiedLabel={t('credits.topUpDetail.copiedTopUpId')}
                    copyFailedLabel={t('credits.topUpDetail.copyFailed')}
                  />
                </DetailRow>
                <DetailRow label={t('credits.topUpDetail.paymentReference')}>
                  <CopyableId
                    value={session.paymentReference}
                    copyLabel={t('credits.topUpDetail.copyPaymentReference')}
                    copiedLabel={t('credits.topUpDetail.copiedPaymentReference')}
                    copyFailedLabel={t('credits.topUpDetail.copyFailed')}
                  />
                </DetailRow>
                {providerTransactionId ? (
                  <DetailRow label={t('credits.topUpDetail.providerTransactionId')}>
                    <CopyableId
                      value={providerTransactionId}
                      copyLabel={t('credits.topUpDetail.copyProviderTransactionId')}
                      copiedLabel={t('credits.topUpDetail.copiedProviderTransactionId')}
                      copyFailedLabel={t('credits.topUpDetail.copyFailed')}
                    />
                  </DetailRow>
                ) : null}
                {session.ledgerEntryId ? (
                  <DetailRow label={t('credits.topUpDetail.ledgerEntryId')}>
                    <CopyableId
                      value={session.ledgerEntryId}
                      copyLabel={t('credits.topUpDetail.copyLedgerEntryId')}
                      copiedLabel={t('credits.topUpDetail.copiedLedgerEntryId')}
                      copyFailedLabel={t('credits.topUpDetail.copyFailed')}
                    />
                  </DetailRow>
                ) : null}
              </dl>
            </>
          ) : null}
        </DialogBody>
        <DialogFooter className="flex justify-end gap-2">
          {canContinuePayment ? (
            <Button
              onClick={() => {
                if (session?.checkoutUrl) {
                  window.location.assign(session.checkoutUrl);
                }
              }}
            >
              {t('credits.continuePayment')}
            </Button>
          ) : null}
          {canRecheck ? (
            <Button
              variant="secondary"
              disabled={recheck.isPending}
              onClick={() => {
                isManualRecheckRef.current = true;
                if (sessionId) recheck.mutate(sessionId);
              }}
            >
              {recheck.isPending ? t('credits.recheckChecking') : t('credits.recheckAction')}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {tCommon('action.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
